import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { engineerAPI, customersAPI, notificationsAPI, installationsAPI, documentsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, STATUS_DISPLAY_NAMES, STATUS_COLORS, PROJECT_STAGES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TimeClockWidget } from '../components/TimeClockWidget';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Calendar,
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  FileText,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Loader2,
  Upload,
  Download,
  Bell,
  Wrench,
} from 'lucide-react';
import { toast } from 'sonner';

export const EngineerPortal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [installations, setInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInstallation, setSelectedInstallation] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [notifyDialog, setNotifyDialog] = useState({ open: false, installation: null });
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [uploadDialog, setUploadDialog] = useState({ open: false, customerId: null });
  const [uploading, setUploading] = useState(false);
  const [uploadNotes, setUploadNotes] = useState('');
  const [activeTab, setActiveTab] = useState('my-customers');
  const [myCustomers, setMyCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(null);

  useEffect(() => {
    fetchInstallations();
    fetchMyCustomers();
  }, []);

  const fetchMyCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await customersAPI.getAll();
      // Filter customers assigned to current user
      const assigned = (response.data || []).filter(
        c => (c.assigned_user_ids || []).includes(user?.id)
      );
      setMyCustomers(assigned);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const getNextStage = (currentStatus) => {
    const idx = PROJECT_STAGES.indexOf(currentStatus);
    if (idx >= 0 && idx < PROJECT_STAGES.length - 1) {
      return PROJECT_STAGES[idx + 1];
    }
    return null;
  };

  const handleChangeStatus = async (customerId, newStatus) => {
    setStatusUpdating(customerId);
    try {
      await customersAPI.updateStatus(customerId, { status: newStatus });
      toast.success(`Status updated to ${STATUS_DISPLAY_NAMES[newStatus] || newStatus}`);
      fetchMyCustomers();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setStatusUpdating(null);
    }
  };

  const fetchInstallations = async () => {
    setLoading(true);
    try {
      const response = await engineerAPI.getMyInstallations();
      setInstallations(response.data);
    } catch (error) {
      console.error('Error fetching installations:', error);
      toast.error('Failed to load installations');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetails = async (customerId) => {
    setCustomerLoading(true);
    try {
      const response = await engineerAPI.getCustomer(customerId);
      setCustomerDetails(response.data);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setCustomerLoading(false);
    }
  };

  const handleSelectInstallation = (installation) => {
    setSelectedInstallation(installation);
    fetchCustomerDetails(installation.customer_id);
  };

  const handleNotifyAdmin = async () => {
    if (!notifyMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setNotifying(true);
    try {
      await notificationsAPI.notifyAdmin({
        message: notifyMessage,
        installation_id: notifyDialog.installation?.id,
        customer_id: notifyDialog.installation?.customer_id,
      });
      toast.success('Admin notified successfully');
      setNotifyDialog({ open: false, installation: null });
      setNotifyMessage('');
    } catch (error) {
      toast.error('Failed to send notification');
    } finally {
      setNotifying(false);
    }
  };

  const handleMoveToNextStage = async (installation) => {
    const currentStatus = installation.status;
    const currentIndex = PROJECT_STAGES.indexOf(currentStatus);
    
    if (currentIndex === -1 || currentIndex >= PROJECT_STAGES.length - 1) {
      toast.error('Cannot move to next stage');
      return;
    }

    const nextStatus = PROJECT_STAGES[currentIndex + 1];

    try {
      await customersAPI.updateStatus(installation.customer_id, { status: nextStatus });
      toast.success(`Moved to ${STATUS_DISPLAY_NAMES[nextStatus]}`);
      fetchInstallations();
      if (selectedInstallation?.id === installation.id) {
        fetchCustomerDetails(installation.customer_id);
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      // Upload the document using the correct API
      await documentsAPI.upload(uploadDialog.customerId, file);
      
      // If there are notes, add them to the customer record
      if (uploadNotes.trim()) {
        const customer = await customersAPI.getById(uploadDialog.customerId);
        const existingNotes = customer.data.notes || '';
        const timestamp = new Date().toLocaleString();
        const newNotes = `${existingNotes}\n\n[${timestamp}] Engineer Note (${user?.name || 'Engineer'}):\n${uploadNotes}`.trim();
        await customersAPI.update(uploadDialog.customerId, { notes: newNotes });
      }
      
      toast.success('File uploaded successfully (hidden from customer portal by default)');
      setUploadDialog({ open: false, customerId: null });
      setUploadNotes('');
      if (customerDetails?.id === uploadDialog.customerId) {
        fetchCustomerDetails(uploadDialog.customerId);
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcomingJobs = installations.filter(i => i.scheduled_date >= today && i.status !== 'completed');
  const completedJobs = installations.filter(i => i.status === 'completed');
  const todayJobs = installations.filter(i => i.scheduled_date === today);

  return (
    <Layout>
      <div className="p-6" data-testid="engineer-portal">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">My Jobs</h1>
          <p className="text-slate-600">View and manage your assigned installations</p>
        </div>

        {/* Time Clock */}
        <div className="mb-6">
          <TimeClockWidget />
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{todayJobs.length}</p>
                  <p className="text-sm text-slate-500">Today's Jobs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{upcomingJobs.length}</p>
                  <p className="text-sm text-slate-500">Upcoming</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{completedJobs.length}</p>
                  <p className="text-sm text-slate-500">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Jobs List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Installations
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="my-customers">My Customers ({myCustomers.length})</TabsTrigger>
                  <TabsTrigger value="upcoming">Installations ({upcomingJobs.length})</TabsTrigger>
                  <TabsTrigger value="completed">Completed ({completedJobs.length})</TabsTrigger>
                </TabsList>

                {/* My Customers Tab */}
                <TabsContent value="my-customers" className="space-y-3">
                  {customersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : myCustomers.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No customers assigned to you</p>
                  ) : (
                    myCustomers.map((customer) => {
                      const nextStage = getNextStage(customer.status);
                      return (
                        <div
                          key={customer.id}
                          className="p-4 border rounded-lg hover:border-blue-300 transition-colors bg-white"
                          data-testid={`my-customer-${customer.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/customers/${customer.id}`)}>
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-slate-900">{customer.first_name} {customer.last_name}</h4>
                                <Badge className={STATUS_COLORS[customer.status] || 'bg-slate-100 text-slate-700'}>
                                  {STATUS_DISPLAY_NAMES[customer.status] || customer.status}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-500">
                                {customer.address && (
                                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{customer.address}, {customer.city}</span>
                                )}
                                {customer.phone && (
                                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phone}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                              {nextStage && customer.status !== 'completed' && (
                                <Button
                                  size="sm"
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  onClick={() => handleChangeStatus(customer.id, nextStage)}
                                  disabled={statusUpdating === customer.id}
                                  data-testid={`next-stage-${customer.id}`}
                                >
                                  {statusUpdating === customer.id ? (
                                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                  ) : (
                                    <ChevronRight className="w-3 h-3 mr-1" />
                                  )}
                                  Move to Next Stage
                                </Button>
                              )}
                              {customer.status === 'completed' && (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Completed
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </TabsContent>

                <TabsContent value="upcoming" className="space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : upcomingJobs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No upcoming installations</p>
                  ) : (
                    upcomingJobs.map((installation) => (
                      <div
                        key={installation.id}
                        onClick={() => handleSelectInstallation(installation)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedInstallation?.id === installation.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                        data-testid={`installation-${installation.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-slate-900">{installation.customer_name}</h3>
                          {installation.scheduled_date === today && (
                            <Badge className="bg-amber-100 text-amber-700">Today</Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(installation.scheduled_date)}
                          {installation.end_date && installation.end_date !== installation.scheduled_date && (
                            <span> - {formatDate(installation.end_date)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-2">
                          <MapPin className="w-4 h-4" />
                          {installation.customer_address}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Clock className="w-4 h-4" />
                          {installation.start_time} - {installation.end_time}
                        </div>
                        <div className="mt-2">
                          <Badge className={STATUS_COLORS[installation.installation_type] || 'bg-slate-100'}>
                            {installation.installation_type}
                          </Badge>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>

                <TabsContent value="completed" className="space-y-3">
                  {completedJobs.length === 0 ? (
                    <p className="text-center text-slate-500 py-8">No completed installations</p>
                  ) : (
                    completedJobs.map((installation) => (
                      <div
                        key={installation.id}
                        onClick={() => handleSelectInstallation(installation)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          selectedInstallation?.id === installation.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                        }`}
                      >
                        <h3 className="font-semibold text-slate-900 mb-2">{installation.customer_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {formatDate(installation.scheduled_date)}
                        </div>
                        <div className="mt-2">
                          <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                        </div>
                      </div>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Selected Installation Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Customer Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedInstallation ? (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Select an installation to view details</p>
                </div>
              ) : customerLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : customerDetails ? (
                <div className="space-y-4">
                  {/* Customer Info */}
                  <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                    <h3 className="font-semibold text-slate-900">
                      {customerDetails.first_name} {customerDetails.last_name}
                    </h3>
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-slate-400" />
                      <span>{customerDetails.address}, {customerDetails.city} {customerDetails.postcode}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <a href={`tel:${customerDetails.phone}`} className="text-blue-600 hover:underline">
                        {customerDetails.phone}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-slate-400" />
                      <a href={`mailto:${customerDetails.email}`} className="text-blue-600 hover:underline">
                        {customerDetails.email}
                      </a>
                    </div>
                  </div>

                  {/* System Info */}
                  <div className="p-4 border rounded-lg space-y-2">
                    <h4 className="font-medium text-slate-900 mb-3">System Details</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-slate-500">System Size:</span>
                      <span className="font-medium">{customerDetails.system_size} kW</span>
                      
                      {customerDetails.panel_manufacturer && (
                        <>
                          <span className="text-slate-500">Panels:</span>
                          <span className="font-medium">{customerDetails.panel_manufacturer}</span>
                        </>
                      )}
                      {customerDetails.inverter_manufacturer && (
                        <>
                          <span className="text-slate-500">Inverter:</span>
                          <span className="font-medium">{customerDetails.inverter_manufacturer}</span>
                        </>
                      )}
                      {customerDetails.battery_manufacturer && (
                        <>
                          <span className="text-slate-500">Battery:</span>
                          <span className="font-medium">{customerDetails.battery_manufacturer}</span>
                        </>
                      )}
                      {customerDetails.consumer_unit_manufacturer && (
                        <>
                          <span className="text-slate-500">Consumer Unit:</span>
                          <span className="font-medium">{customerDetails.consumer_unit_manufacturer}</span>
                        </>
                      )}
                      <span className="text-slate-500">Roof Type:</span>
                      <span className="font-medium">{customerDetails.roof_type?.replace('_', ' ')}</span>
                    </div>
                    {customerDetails.notes && (
                      <div className="mt-3 pt-3 border-t">
                        <span className="text-slate-500 text-sm">Notes:</span>
                        <p className="text-sm mt-1">{customerDetails.notes}</p>
                      </div>
                    )}
                  </div>

                  {/* Current Status */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div>
                      <span className="text-sm text-slate-500">Current Status</span>
                      <p className="font-semibold text-slate-900">
                        {STATUS_DISPLAY_NAMES[customerDetails.status]}
                      </p>
                    </div>
                    <Badge className={STATUS_COLORS[customerDetails.status]}>
                      {STATUS_DISPLAY_NAMES[customerDetails.status]}
                    </Badge>
                  </div>

                  {/* Documents */}
                  {customerDetails.documents && customerDetails.documents.length > 0 && (
                    <div className="p-4 border rounded-lg">
                      <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Documents ({customerDetails.documents.length})
                      </h4>
                      <div className="space-y-2">
                        {customerDetails.documents.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                            <span className="text-sm truncate">{doc.filename}</span>
                            <Button variant="ghost" size="sm" asChild>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                <Download className="w-4 h-4" />
                              </a>
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col gap-2 pt-4 border-t">
                    <Button
                      onClick={() => setUploadDialog({ open: true, customerId: customerDetails.id })}
                      variant="outline"
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document/Photo
                    </Button>
                    
                    {selectedInstallation?.status !== 'completed' && (
                      <>
                        <Button
                          onClick={() => handleMoveToNextStage(selectedInstallation)}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                        >
                          <ChevronRight className="w-4 h-4 mr-2" />
                          Move to Next Stage
                        </Button>
                        <Button
                          onClick={() => setNotifyDialog({ open: true, installation: selectedInstallation })}
                          variant="outline"
                          className="w-full"
                        >
                          <Bell className="w-4 h-4 mr-2" />
                          Notify Admin
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">Failed to load customer details</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Notify Admin Dialog */}
        <Dialog open={notifyDialog.open} onOpenChange={(open) => setNotifyDialog({ open, installation: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Notify Admin</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Send a notification to admin about this installation
                {notifyDialog.installation && (
                  <span className="font-medium"> for {notifyDialog.installation.customer_name}</span>
                )}
              </p>
              <Textarea
                placeholder="Enter your message (e.g., 'Installation complete, awaiting sign-off' or 'Issue found - need assistance')"
                value={notifyMessage}
                onChange={(e) => setNotifyMessage(e.target.value)}
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNotifyDialog({ open: false, installation: null })}>
                Cancel
              </Button>
              <Button onClick={handleNotifyAdmin} disabled={notifying}>
                {notifying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                Send Notification
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={uploadDialog.open} onOpenChange={(open) => {
          setUploadDialog({ open, customerId: uploadDialog.customerId });
          if (!open) setUploadNotes('');
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Document/Photo</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Files uploaded here will be saved to the customer's documents but hidden from their portal by default.
              </p>
              <Input
                type="file"
                onChange={handleFileUpload}
                disabled={uploading}
                accept="image/*,.pdf,.doc,.docx"
                data-testid="engineer-file-input"
              />
              <div>
                <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
                <Textarea
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder="Add any notes about this upload (e.g., 'Photo of completed installation', 'Issue found with wiring')"
                  className="mt-1"
                  rows={3}
                  data-testid="engineer-upload-notes"
                />
                <p className="text-xs text-slate-500 mt-1">Notes will be added to the customer's record</p>
              </div>
              {uploading && (
                <div className="flex items-center justify-center p-4 bg-blue-50 rounded-lg">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-blue-700">Uploading...</span>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
